import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'

import {
    ArrowLeft,
    PanelRightClose,
    PanelRightOpen,
    CirclePlay,
    X
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { usePreferences } from '@/components/generals/providers'
import { NavList } from '@/components/generals/navigation'
import { Logo } from '@/components/generals/logo'
import { ConfluenceHint } from '@/components/generals/confluence-hint'
import { HintsPanel } from '@/components/generals/hints-toggle'

const HelpCard: FC = (): ReactNode => {
    return (
        <Link
            href='/knowledge/confluence-knowledge-training'
            className='group flex flex-col gap-2 rounded-lg border border-primary/20 bg-primary-soft/60 p-3 transition-colors hover:border-primary/40 hover:bg-primary-soft'
        >
            <span className='flex items-center gap-2 text-small font-bold text-navy'>
                <CirclePlay className='size-4 text-primary' aria-hidden='true' />
                نخستین دانش خود را ثبت می‌کنید؟
            </span>

            <span className='text-caption text-muted-foreground'>
                آموزش ۴۰ دقیقه‌ای ثبت و سازمان‌دهی دانش را ببینید.
            </span>

            <span className='flex items-center gap-1 text-caption font-semibold text-primary-strong'>
                مشاهده آموزش
                <ArrowLeft className='size-3.5 transition-transform group-hover:-translate-x-0.5' aria-hidden='true' />
            </span>
        </Link>
    )
}

export const Sidebar: FC = (): ReactNode => {
    const { sidebarCollapsed: collapsed, setSidebarCollapsed } = usePreferences()
    const ToggleIcon = collapsed ? PanelRightOpen : PanelRightClose

    return (
        <aside
            className={cn(
                'sticky top-16 z-30 hidden h-[calc(100dvh-4rem)] shrink-0 flex-col border-e border-border bg-card transition-[width] duration-200 lg:flex',
                collapsed ? 'w-[76px]' : 'w-64'
            )}
        >
            <nav aria-label='منوی اصلی' className='no-scrollbar relative flex-1 overflow-y-auto px-3 pb-4 pt-6'>
                <ConfluenceHint id='sidebar-nav' className='top-1' />
                <NavList collapsed={collapsed} />
            </nav>

            <div className='flex flex-col gap-3 border-t border-border p-3'>
                {!collapsed && <HelpCard />}

                <button
                    type='button'
                    onClick={() => setSidebarCollapsed(!collapsed)}
                    aria-expanded={!collapsed}
                    className={cn(
                        'flex h-9 cursor-pointer items-center gap-2 rounded-lg text-small font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                        collapsed ? 'justify-center' : 'px-3'
                    )}
                    aria-label={collapsed ? 'باز کردن منو' : 'جمع کردن منو'}
                    title={collapsed ? 'باز کردن منو' : undefined}
                >
                    <ToggleIcon className='size-4' aria-hidden='true' />
                    {!collapsed && <span>جمع کردن منو</span>}
                </button>
            </div>
        </aside>
    )
}

interface MobileNavProps {
    open: boolean
    onClose: () => void
}

export const MobileNav: FC<MobileNavProps> = ({ open, onClose }): ReactNode => {
    const router = useRouter()
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        const previous = document.body.style.overflow
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', handleKey)
        closeRef.current?.focus()

        return () => {
            document.body.style.overflow = previous
            document.removeEventListener('keydown', handleKey)
        }
    }, [open, onClose])

    useEffect(() => {
        router.events.on('routeChangeStart', onClose)

        return () => router.events.off('routeChangeStart', onClose)
    }, [router.events, onClose])

    if (!open) return null

    return (
        <div className='fixed inset-0 z-50 lg:hidden' role='dialog' aria-modal='true' aria-label='منوی اصلی'>
            <button
                type='button'
                tabIndex={-1}
                onClick={onClose}
                className='absolute inset-0 cursor-default bg-navy/45 animate-in fade-in duration-200'
                aria-label='بستن منو'
            />

            <div className='absolute inset-y-0 start-0 flex w-[86%] max-w-xs flex-col bg-card shadow-pop animate-in slide-in-from-right duration-200'>
                <div className='flex h-16 shrink-0 items-center justify-between border-b border-border px-4'>
                    <Logo />

                    <button
                        ref={closeRef}
                        type='button'
                        onClick={onClose}
                        className='flex size-10 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45'
                        aria-label='بستن منو'
                    >
                        <X className='size-5' aria-hidden='true' />
                    </button>
                </div>

                <nav aria-label='منوی اصلی' className='flex-1 overflow-y-auto px-4 py-5'>
                    <NavList onNavigate={onClose} />
                </nav>

                <div className='flex flex-col gap-4 border-t border-border p-4'>
                    <HelpCard />

                    <details className='group rounded-lg border border-border'>
                        <summary className='flex cursor-pointer list-none items-center justify-between px-3 py-2.5 text-small font-semibold text-navy [&::-webkit-details-marker]:hidden'>
                            راهنمای پیاده‌سازی Confluence
                            <span className='text-caption text-muted-foreground group-open:hidden'>نمایش</span>
                        </summary>

                        <div className='border-t border-border p-3'>
                            <HintsPanel onNavigate={onClose} />
                        </div>
                    </details>
                </div>
            </div>
        </div>
    )
}
